/**
 * Session refresh + company-access recovery (backend H20, 2026-10-27).
 *
 * The backend re-checks on every tenant request, and on /ws/dashboard, that the user
 * still belongs to the company in their access token. When they don't (HR exit, role
 * removed, company-scoped GDPR erase) it answers 403 with
 * `error_code: "COMPANY_ACCESS_REVOKED"`, and the socket closes with 1008.
 *
 * Recovery = ONE token refresh (the backend moves the session to a company the user still
 * belongs to, or answers 401 when none is left), then a reload so every store rehydrates,
 * then a one-time notice. It runs at most once per COMPANY_RECOVERY_COOLDOWN_MS, the same
 * timestamp for the HTTP and the WebSocket path. The tab that finished it tells the other
 * tabs (BroadcastChannel 'auth', storage-event fallback) so they follow, once per id.
 *
 * The WebSocket stops trying after a recovery that didn't help: a 1008 within
 * RECOVERY_SETTLE_MS (2 min) of a successful recovery in this tab leaves the socket closed and
 * shows "Live updates paused, reload to retry" (no refresh-and-reload every cooldown). A 1008
 * inside the cooldown otherwise schedules one reconnect for when it ends.
 *
 * `refreshSession()` is the app's only way to call POST /auth/refresh. The refresh token
 * rotates on use, so two parallel refreshes would log the user out: one promise per tab,
 * and `navigator.locks` across tabs. A tab that waited for another tab's refresh reuses its
 * result instead of refreshing again. Every wait is bounded (REFRESH_LOCK_WAIT_MS) and the
 * request itself times out (REFRESH_TIMEOUT_MS); a tab whose wait aborts never POSTs without
 * the lock (see refreshAfterLockAbort). Without Web Locks the in-tab promise is all there is
 * (a BroadcastChannel fallback would not add coverage: every browser without Web Locks
 * also lacks BroadcastChannel, both arrived in Safari 15.4).
 */

import axios from 'axios';
// The app's i18next instance (initialised by @/i18n/config at boot). Imported from the package,
// not the config module, so importing the API client never touches i18n set-up.
import i18n from 'i18next';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';

const API_BASE = import.meta.env.VITE_API_URL || '';

export const COMPANY_ACCESS_REVOKED = 'COMPANY_ACCESS_REVOKED';
export const COMPANY_RECOVERY_COOLDOWN_MS = 60_000;

const RECOVERY_AT_KEY = 'company_access_recovery_at'; // sessionStorage (this tab)
const NOTICE_KEY = 'company_access_notice'; // sessionStorage, read once after the reload
const SHARED_REFRESH_KEY = 'auth_refresh_result'; // localStorage (all tabs)
const SHARED_REUSE_MS = 10_000;
export const REFRESH_LOCK_NAME = 'auth-refresh';
/** A refresh request gives up after this long, so a hung one can't hold the lock. */
export const REFRESH_TIMEOUT_MS = 15_000;
/** Waiting for another tab's lock gives up after this long; this tab then refreshes itself. */
export const REFRESH_LOCK_WAIT_MS = 20_000;
/** Cross-tab "this session moved company" message (BroadcastChannel, storage-event fallback). */
export const AUTH_CHANNEL = 'auth';
const RECOVERED_EVENT_KEY = 'auth_company_recovered'; // localStorage, storage-event fallback
const HANDLED_IDS_KEY = 'company_access_recovery_ids'; // sessionStorage (this tab)
const RECOVERED_AT_KEY = 'company_access_recovered_at'; // sessionStorage: last successful one
/** A 1008 this soon after a successful recovery means the recovery didn't help: stop. */
export const RECOVERY_SETTLE_MS = 120_000;

export interface RefreshResult {
  /** The company the new access token carries; null when none. */
  companyId: string | null;
}

async function postRefresh(): Promise<RefreshResult> {
  const res = await axios.post(
    `${API_BASE}/api/v1/auth/refresh`,
    {},
    { withCredentials: true, timeout: REFRESH_TIMEOUT_MS },
  );
  const companyId = (res?.data as { company_id?: string | null } | undefined)?.company_id;
  return { companyId: companyId ?? null };
}

function readShared(): { at: number; result: RefreshResult } | null {
  try {
    const raw = localStorage.getItem(SHARED_REFRESH_KEY);
    return raw ? (JSON.parse(raw) as { at: number; result: RefreshResult }) : null;
  } catch {
    return null;
  }
}

function writeShared(result: RefreshResult): void {
  try {
    localStorage.setItem(SHARED_REFRESH_KEY, JSON.stringify({ at: Date.now(), result }));
  } catch {
    // storage unavailable: the lock still serialises
  }
}

/** A signal that aborts after REFRESH_LOCK_WAIT_MS (AbortController fallback where
 * AbortSignal.timeout is missing). */
export function lockWaitSignal(): AbortSignal | undefined {
  try {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      return AbortSignal.timeout(REFRESH_LOCK_WAIT_MS);
    }
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      setTimeout(
        () => controller.abort(new DOMException('lock wait timed out', 'TimeoutError')),
        REFRESH_LOCK_WAIT_MS,
      );
      return controller.signal;
    }
  } catch {
    // fall through: wait without a bound
  }
  return undefined;
}

function isAbort(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'AbortError' || name === 'TimeoutError';
}

/** A refresh another tab finished after this tab started waiting (its cookies are ours). */
function reusable(waitingSince: number): RefreshResult | null {
  const last = readShared();
  if (last && last.at >= waitingSince && Date.now() - last.at < SHARED_REUSE_MS) {
    return last.result;
  }
  return null;
}

/** Holding the lock: reuse a fresh result, or refresh and share it. */
async function refreshHoldingLock(waitingSince: number): Promise<RefreshResult> {
  const reused = reusable(waitingSince);
  if (reused) return reused;
  const result = await postRefresh();
  writeShared(result);
  return result;
}

const NOT_ACQUIRED = Symbol('not-acquired');

/** Thrown when the lock never became free: callers sign the user out. */
export class RefreshLockUnavailable extends Error {
  constructor() {
    super('auth refresh lock unavailable');
    this.name = 'RefreshLockUnavailable';
  }
}

/**
 * The bounded wait for another tab's lock aborted. Never POST /auth/refresh without the lock
 * while another tab may hold it (the refresh token rotates): reuse a result that tab shared;
 * else take the lock only if it is free right now; else wait ONE more bounded period; else
 * give up (RefreshLockUnavailable, the caller signs out).
 */
async function refreshAfterLockAbort(
  locks: LockManager,
  waitingSince: number,
): Promise<RefreshResult> {
  const reused = reusable(waitingSince);
  if (reused) return reused;

  const immediate = await locks.request(
    REFRESH_LOCK_NAME,
    { ifAvailable: true },
    async (lock: Lock | null) => (lock ? refreshHoldingLock(waitingSince) : NOT_ACQUIRED),
  );
  if (immediate !== NOT_ACQUIRED) return immediate as RefreshResult;

  const signal = lockWaitSignal();
  try {
    return await locks.request(REFRESH_LOCK_NAME, signal ? { signal } : {}, () =>
      refreshHoldingLock(waitingSince),
    );
  } catch (err) {
    if (isAbort(err)) throw new RefreshLockUnavailable();
    throw err;
  }
}

async function refreshAcrossTabs(): Promise<RefreshResult> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return postRefresh();
  const waitingSince = Date.now();
  const signal = lockWaitSignal();
  try {
    return await locks.request(REFRESH_LOCK_NAME, signal ? { signal } : {}, () =>
      // A holder that failed wrote nothing, so this tab simply tries itself.
      refreshHoldingLock(waitingSince),
    );
  } catch (err) {
    // Gave up waiting before getting the lock. Errors from our own refresh propagate.
    if (isAbort(err)) return refreshAfterLockAbort(locks, waitingSince);
    throw err;
  }
}

let inFlight: Promise<RefreshResult> | null = null;

/** One POST /auth/refresh at a time for the whole app (and across tabs). */
export function refreshSession(): Promise<RefreshResult> {
  if (!inFlight) {
    inFlight = refreshAcrossTabs().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Navigation side effects, behind an object so tests can replace them. */
export const sessionNavigation = {
  reload: () => window.location.reload(),
  toLogin: () => {
    useAuthStore.getState().logout();
    window.location.href = '/login';
  },
};

function lastRecoveryAt(): number {
  try {
    return Number(sessionStorage.getItem(RECOVERY_AT_KEY) || 0);
  } catch {
    return 0;
  }
}

function markRecovery(now: number): void {
  try {
    sessionStorage.setItem(RECOVERY_AT_KEY, String(now));
  } catch {
    // storage unavailable: the in-memory flag still prevents a second refresh
  }
}

function companyName(companyId: string | null | undefined): string | null {
  if (!companyId) return null;
  const c = useAuthStore.getState().companies.find((x) => x.company_id === companyId);
  return c?.company_name ?? null;
}

let recoveryStarted = false;

/** True within RECOVERY_SETTLE_MS of a successful recovery in this tab. */
export function recoveredRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RECOVERED_AT_KEY) || 0);
    return at > 0 && Date.now() - at < RECOVERY_SETTLE_MS;
  } catch {
    return false;
  }
}

/** The WebSocket gave up after a recovery that didn't help: tell the user, once. */
export function showLiveUpdatesPaused(): void {
  const translated = i18n.isInitialized ? i18n.t('notifications.live_updates_paused') : undefined;
  const message =
    typeof translated === 'string' && translated && translated !== 'notifications.live_updates_paused'
      ? translated
      : 'Live updates paused, reload to retry.';
  useNotificationStore.getState().showWarning(message);
}

/** ms until another recovery may run (0 when it may run now). Shared HTTP/WebSocket cooldown. */
export function companyRecoveryCooldownRemaining(): number {
  return Math.max(0, lastRecoveryAt() + COMPANY_RECOVERY_COOLDOWN_MS - Date.now());
}

interface RecoveredMessage {
  type: 'company-access-recovered';
  id: string;
  companyId: string | null;
  from: string | null;
}

function newRecoveryId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function handledIds(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(HANDLED_IDS_KEY) || '[]') as string[];
  } catch {
    return [];
  }
}

/** True the first time this tab sees ``id`` (loop guard: once per recovery id). */
function claimRecoveryId(id: string): boolean {
  const ids = handledIds();
  if (ids.includes(id)) return false;
  try {
    sessionStorage.setItem(HANDLED_IDS_KEY, JSON.stringify([...ids, id].slice(-20)));
  } catch {
    // without storage the in-memory reload still ends this tab's state
  }
  return true;
}

/** Point the (persisted) store at the token's company with a fresh list, leave a notice. */
async function applyRecoveredCompany(companyId: string | null, fromName: string | null) {
  try {
    const me = await axios.get(`${API_BASE}/api/v1/auth/me`, {
      withCredentials: true,
      timeout: REFRESH_TIMEOUT_MS,
    });
    const data = me.data as {
      companies?: Parameters<ReturnType<typeof useAuthStore.getState>['setUser']>[1];
      platform_role?: Parameters<ReturnType<typeof useAuthStore.getState>['setUser']>[3];
    };
    const state = useAuthStore.getState();
    if (state.user) {
      state.setUser(state.user, data.companies ?? [], companyId ?? undefined, data.platform_role ?? null);
    }
  } catch (err) {
    // The reload's own session check will sort it out. Error type only, never the payload.
    console.warn(
      'company-access recovery: /auth/me failed',
      (err as { name?: string } | null)?.name ?? typeof err,
    );
  }
  try {
    sessionStorage.setItem(RECOVERED_AT_KEY, String(Date.now()));
  } catch {
    // no settle window then
  }
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ from: fromName, to: companyName(companyId) }));
  } catch {
    // no notice then
  }
}

function announceRecovery(message: RecoveredMessage): void {
  let sent = false;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(AUTH_CHANNEL);
      ch.postMessage(message);
      ch.close();
      sent = true;
    }
  } catch {
    sent = false;
  }
  if (!sent) {
    try {
      localStorage.setItem(RECOVERED_EVENT_KEY, JSON.stringify({ ...message, at: Date.now() }));
    } catch {
      // no other tab will hear it; each recovers on its own next 403/1008
    }
  }
}

/** Another tab finished a recovery: move this tab to the same company, once per id. */
async function onRecoveredElsewhere(message: RecoveredMessage | null): Promise<void> {
  if (!message || message.type !== 'company-access-recovered' || !message.id) return;
  if (!claimRecoveryId(message.id)) return;
  markRecovery(Date.now()); // this tab shouldn't start its own refresh right after
  const fromName = companyName(useAuthStore.getState().activeCompanyId) ?? message.from;
  await applyRecoveredCompany(message.companyId, fromName);
  sessionNavigation.reload();
}

let listening = false;

/** Install the cross-tab listener (once). Returns a cleanup for tests. */
export function listenForCompanyAccessRecovery(): () => void {
  if (listening) return () => {};
  listening = true;
  let channel: BroadcastChannel | null = null;
  const onMessage = (e: MessageEvent) => void onRecoveredElsewhere(e.data as RecoveredMessage);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== RECOVERED_EVENT_KEY || !e.newValue) return;
    try {
      void onRecoveredElsewhere(JSON.parse(e.newValue) as RecoveredMessage);
    } catch {
      // ignore malformed
    }
  };
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(AUTH_CHANNEL);
      channel.onmessage = onMessage;
    }
  } catch {
    channel = null;
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listening = false;
    channel?.close();
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/**
 * Recover from a lost company: refresh once, sync the store to the token's company, reload.
 * A failed refresh signs out. Returns false when a recovery already ran within the cooldown
 * (the caller then shows its normal error, and must not retry).
 */
export function recoverCompanyAccess(): boolean {
  if (recoveryStarted) return true;
  const now = Date.now();
  if (now - lastRecoveryAt() < COMPANY_RECOVERY_COOLDOWN_MS) return false;
  recoveryStarted = true;
  markRecovery(now);

  const fromName = companyName(useAuthStore.getState().activeCompanyId);

  refreshSession()
    .then(async ({ companyId }) => {
      // The store is persisted, so a reload alone would keep the old company selected:
      // set the fresh company list and the token's company first.
      await applyRecoveredCompany(companyId, fromName);
      // Every other tab of this session now holds a token for the new company too: tell
      // them, so none keeps showing (or writing to) the old one. Once per recovery id.
      const id = newRecoveryId();
      claimRecoveryId(id);
      announceRecovery({ type: 'company-access-recovered', id, companyId, from: fromName });
      sessionNavigation.reload();
    })
    .catch(() => sessionNavigation.toLogin());
  return true;
}

/** After a recovery reload: show the one-time notice, then forget it. */
export function showCompanyAccessNotice(): void {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(NOTICE_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  let notice: { from?: string | null; to?: string | null } = {};
  try {
    notice = JSON.parse(raw);
  } catch {
    return;
  }
  const moved = Boolean(notice.from && notice.to);
  const fallback = moved
    ? `Your access to ${notice.from} changed; you're now in ${notice.to}.`
    : 'Your access changed.';
  const translated = i18n.isInitialized
    ? moved
      ? i18n.t('auth.company_access_moved', { from: notice.from, to: notice.to })
      : i18n.t('auth.company_access_changed')
    : undefined;
  const message = typeof translated === 'string' && translated ? translated : fallback;
  useNotificationStore.getState().showInfo(message);
}

/** Test hook. `keepCooldown` models a reload: in-memory state goes, sessionStorage stays. */
export function resetSessionRecoveryForTests(opts?: { keepCooldown?: boolean }): void {
  recoveryStarted = false;
  inFlight = null;
  if (opts?.keepCooldown) return;
  try {
    sessionStorage.removeItem(RECOVERY_AT_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
    sessionStorage.removeItem(HANDLED_IDS_KEY);
    sessionStorage.removeItem(RECOVERED_AT_KEY);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(SHARED_REFRESH_KEY);
  } catch {
    // ignore
  }
}

/** Test hook: deliver a cross-tab message as if another tab had sent it. */
export function deliverRecoveryMessageForTests(message: unknown): Promise<void> {
  return onRecoveredElsewhere(message as RecoveredMessage);
}
